import { useMemo } from "react";
import { buildSaudiPlateRows } from "../../utils/plateConversions";
import plateSaudiLogo from "../../assets/plate_saudi_logo.png";
import plateSaudiLogoDown from "../../assets/plate_saudi_logodown.png";

export type VehicleLicensePlateSize = "xs" | "sm" | "md";

export interface VehicleLicensePlateProps {
  plateNumbers?: string | number | null;
  plateLetters?: string | null;
  letter1?: string;
  letter2?: string;
  letter3?: string;
  size?: VehicleLicensePlateSize;
  className?: string;
  ariaLabel?: string;
}

/**
 * Saudi plate: table-style borders (nums | letters | rail), Arabic row on top, Latin below.
 */
const VehicleLicensePlate = ({
  plateNumbers,
  plateLetters,
  letter1,
  letter2,
  letter3,
  size = "md",
  className = "",
  ariaLabel,
}: VehicleLicensePlateProps) => {
  const rows = useMemo(
    () =>
      buildSaudiPlateRows(plateNumbers, {
        letter1,
        letter2,
        letter3,
        plateLetters: plateLetters ?? undefined,
      }),
    [plateNumbers, plateLetters, letter1, letter2, letter3]
  );

  const sizeClass =
    size === "xs"
      ? "ac-vehicle-plate--xs"
      : size === "sm"
        ? "ac-vehicle-plate--sm"
        : "ac-vehicle-plate--md";

  const rootClass = [
    "ac-saudi-plate",
    "ac-vehicle-plate",
    sizeClass,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass} dir="ltr" role="img" aria-label={ariaLabel}>
      <div className="ac-saudi-plate__cell ac-saudi-plate__cell--ar-nums">
        <span className="ac-saudi-plate__nums">{rows.arNums}</span>
      </div>
      <div className="ac-saudi-plate__cell ac-saudi-plate__cell--ar-letters">
        <span className="ac-saudi-plate__letters ac-saudi-plate__letters--ar">
          {rows.arLetters}
        </span>
      </div>
      <div className="ac-saudi-plate__cell ac-saudi-plate__cell--en-nums">
        <span className="ac-saudi-plate__nums">{rows.enNums}</span>
      </div>
      <div className="ac-saudi-plate__cell ac-saudi-plate__cell--en-letters">
        <span className="ac-saudi-plate__letters ac-saudi-plate__letters--latin">
          {rows.enLetters}
        </span>
      </div>
      <aside className="ac-saudi-plate__rail" aria-hidden>
        <img
          src={plateSaudiLogo}
          alt=""
          className="ac-saudi-plate__mark ac-saudi-plate__mark--top"
        />
        <span className="ac-saudi-plate__country">السعودية</span>
        <div className="ac-saudi-plate__ksa">
          <span>K</span>
          <span>S</span>
          <span>A</span>
        </div>
        <img
          src={plateSaudiLogoDown}
          alt=""
          className="ac-saudi-plate__mark ac-saudi-plate__mark--bottom"
        />
        <span className="ac-saudi-plate__dot" />
      </aside>
    </div>
  );
};

export default VehicleLicensePlate;
